import { useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { ActiveSession } from "@autorepair/contracts";
import { authApi, setUnauthorizedHandler } from "./api";
import { AuthContext } from "./authContext";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<ActiveSession | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // A 401 from ANY request (not just this mount check) means the session is
  // no longer valid — expired, revoked, or the API process restarted (see
  // BOOT_ID in apps/api/src/auth/session-cookie.ts). Dropping session here
  // sends every `Protected` route to /login instead of leaving whichever
  // page made the failing request stuck showing a generic error. Also
  // clears the query cache — see logout()'s own comment on why that
  // matters just as much here as it does there.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      queryClient.clear();
      setSession(null);
    });
    return () => setUnauthorizedHandler(null);
  }, [queryClient]);

  useEffect(() => {
    let cancelled = false;
    authApi
      .session()
      .then((res) => {
        if (!cancelled) setSession(res.session);
      })
      .catch(() => {
        if (!cancelled) setSession(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function logout(): Promise<void> {
    await authApi.logout().catch(() => undefined);
    // Every cached query (customers, invoices, dashboard, ...) is keyed by
    // resource id only, never by tenant — without this, logging into a
    // DIFFERENT tenant next (or switching tenants via LoginPage's
    // pickTenant, for a user with more than one) would keep rendering
    // whatever this tenant's data was still sitting in the cache, until
    // each individual query happened to refetch. The backend's own RLS
    // still only ever returns the right tenant's rows either way — this
    // is a frontend display leak, not a real data-access one — but it's
    // still a real cross-tenant leak in the browser and must never happen.
    queryClient.clear();
    setSession(null);
  }

  /** Same cache/session cleanup as logout() above, just backed by
   * POST /auth/logout-all-devices instead — revokes every device's
   * session, including this one, so this browser ends up logged out too. */
  async function logoutAllDevices(): Promise<void> {
    await authApi.logoutAllDevices().catch(() => undefined);
    queryClient.clear();
    setSession(null);
  }

  return (
    <AuthContext.Provider value={{ session, loading, setSession, logout, logoutAllDevices }}>{children}</AuthContext.Provider>
  );
}
